import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_MACHINES } from '../../../config/collections'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  MACHINE_OUTPUT_PERIODS,
  MACHINE_OUTPUT_UNITS,
  MACHINE_TYPES,
} from '../../../lib/machines'
import type {
  MachineOutputPeriod,
  MachineOutputUnit,
  MachineRecord,
  MachineType,
} from '../../../types/records'
import { Alert, ButtonLink, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { btn, cn, formActionsClass, formGridClass } from '../../../lib/ui'

export function MachineFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [type, setType] = useState<MachineType>('toilet_paper')
  const [number, setNumber] = useState('1')
  const [outputQuantity, setOutputQuantity] = useState('')
  const [outputUnit, setOutputUnit] = useState<MachineOutputUnit>('rolls')
  const [outputPeriod, setOutputPeriod] = useState<MachineOutputPeriod>('hour')
  const [shiftHours, setShiftHours] = useState('8')
  const [workingHours, setWorkingHours] = useState('22')
  const [maintenanceHours, setMaintenanceHours] = useState('2')
  const [sort, setSort] = useState('10')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const load = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_MACHINES).getOne<MachineRecord>(id)
      setType(row.type)
      setNumber(String(row.number))
      setOutputQuantity(String(row.output_quantity))
      setOutputUnit(row.output_unit)
      setOutputPeriod(row.output_period)
      setShiftHours(String(row.shift_hours))
      setWorkingHours(String(row.working_hours_per_day))
      setMaintenanceHours(String(row.maintenance_hours_per_day))
      setSort(String(row.sort))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  async function checkDuplicate(typeVal: MachineType, numberVal: number): Promise<boolean> {
    const filter = `type = "${typeVal}" && number = ${numberVal}${isNew ? '' : ` && id != "${id}"`}`
    const dup = await pb.collection(COL_MACHINES).getList<MachineRecord>(1, 1, { filter })
    return dup.items.length > 0
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')

    const numberVal = Number(number)
    const outputQty = Number(String(outputQuantity).replace(',', '.'))
    const shiftVal = Number(shiftHours)
    const workingVal = Number(String(workingHours).replace(',', '.'))
    const maintenanceVal = Number(String(maintenanceHours).replace(',', '.'))
    const sortVal = Number(sort)

    if (!Number.isInteger(numberVal) || numberVal < 1) {
      setErr('Номер станка — целое число от 1.')
      return
    }
    if (!Number.isFinite(outputQty) || outputQty < 0) {
      setErr('Укажите корректную производительность.')
      return
    }
    if (!Number.isInteger(shiftVal) || shiftVal < 1 || shiftVal > 24) {
      setErr('Длина смены — целое число от 1 до 24.')
      return
    }
    if (!Number.isFinite(workingVal) || workingVal < 0 || workingVal > 24) {
      setErr('Рабочие часы — число от 0 до 24.')
      return
    }
    if (!Number.isFinite(maintenanceVal) || maintenanceVal < 0 || maintenanceVal > 24) {
      setErr('Часы техработ — число от 0 до 24.')
      return
    }
    if (maintenanceVal > workingVal) {
      setErr('Часы техработ не могут превышать рабочие часы.')
      return
    }
    if (!Number.isInteger(sortVal) || sortVal < 0) {
      setErr('Порядок сортировки — целое число от 0.')
      return
    }

    try {
      if (await checkDuplicate(type, numberVal)) {
        setErr('Станок с таким типом и номером уже существует.')
        return
      }

      const body = {
        type,
        number: numberVal,
        output_quantity: outputQty,
        output_unit: outputUnit,
        output_period: outputPeriod,
        shift_hours: shiftVal,
        working_hours_per_day: workingVal,
        maintenance_hours_per_day: maintenanceVal,
        sort: sortVal,
      }

      if (isNew) {
        await pb.collection(COL_MACHINES).create(body)
      } else {
        await pb.collection(COL_MACHINES).update(id!, body)
      }
      navigate('/admin/administration/machines', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новый станок' : 'Редактирование станка'}
        lead="Параметры линии и производительности отображаются на сводке производства."
      />
      {err && <Alert kind="err">{err}</Alert>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={cn(formGridClass, 'max-w-[40rem]')}>
          <FormField label="Тип линии" htmlFor="machine-type">
            <select
              id="machine-type"
              value={type}
              onChange={(e) => setType(e.target.value as MachineType)}
              required
              {...formInputProps()}
            >
              {MACHINE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Номер станка" htmlFor="machine-number">
            <input
              id="machine-number"
              type="number"
              min={1}
              step={1}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Производительность" htmlFor="machine-output-qty">
            <input
              id="machine-output-qty"
              type="number"
              min={0}
              step="any"
              value={outputQuantity}
              onChange={(e) => setOutputQuantity(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Единица выпуска" htmlFor="machine-output-unit">
            <select
              id="machine-output-unit"
              value={outputUnit}
              onChange={(e) => setOutputUnit(e.target.value as MachineOutputUnit)}
              required
              {...formInputProps()}
            >
              {MACHINE_OUTPUT_UNITS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Период выпуска" htmlFor="machine-output-period">
            <select
              id="machine-output-period"
              value={outputPeriod}
              onChange={(e) => setOutputPeriod(e.target.value as MachineOutputPeriod)}
              required
              {...formInputProps()}
            >
              {MACHINE_OUTPUT_PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Длина смены, ч" htmlFor="machine-shift-hours">
            <input
              id="machine-shift-hours"
              type="number"
              min={1}
              max={24}
              step={1}
              value={shiftHours}
              onChange={(e) => setShiftHours(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Рабочих часов в день" htmlFor="machine-working-hours">
            <input
              id="machine-working-hours"
              type="number"
              min={0}
              max={24}
              step="any"
              value={workingHours}
              onChange={(e) => setWorkingHours(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Техработы, ч/сут" htmlFor="machine-maintenance-hours">
            <input
              id="machine-maintenance-hours"
              type="number"
              min={0}
              max={24}
              step="any"
              value={maintenanceHours}
              onChange={(e) => setMaintenanceHours(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Порядок на сводке" htmlFor="machine-sort">
            <input
              id="machine-sort"
              type="number"
              min={0}
              step={1}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <ButtonLink to="/admin/administration/machines" variant="ghost">
              Отмена
            </ButtonLink>
          </div>
        </form>
      )}
    </PageShell>
  )
}
